/**
 * Layout — chrome wrapper (sidebar + main pane) for every authenticated route.
 *
 * Iter 318 (2026-09-21): matched the live env's branding + section order + all
 * 9 clinic tabs per Davinder's screenshot. Kept the preview's clean section
 * grouping, role-based filtering, new-inquiry pulse badge, and OnboardingTour
 * auto-launch. `data-testid="sidebar"` preserved for the tour spotlight.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import OnboardingTour from './OnboardingTour';
import OrgSwitcher from './OrgSwitcher';
import { getOrg, setOrg, ORG_META, LUDO_PATHS } from '../lib/org';
import {
  LayoutDashboard, Users as UsersIcon, MessageSquare, CalendarCheck2,
  Mail, ClipboardList, FileText, TrendingUp, BarChart3, Award,
  FileSpreadsheet, Building2, ShieldCheck, Wallet, HeartHandshake,
  DollarSign, Upload, Link2, Settings, HelpCircle, UserCog, LogOut,
  Trophy, Star, Anchor, Flame, Landmark, TreePine, Sunrise, Cross, Home,
  Menu, PanelLeftClose, Pin, PinOff, CalendarDays,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND_LOGO = 'https://customer-assets-jai6qajn.emergentagent.net/job_pediatric-dispatch/artifacts/ocaqpxqe___circle%20logo_dark%20purple.jpg';

// Sidebar sections. Order + labels + tabs mirror live env (2026-09-21 screenshot).
const NAV = [
  { section: 'Core',
    items: [
      { to: '/dashboard',    label: 'Dashboard',      icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/pipeline',     label: 'Client Pipeline', icon: UsersIcon,      testid: 'nav-pipeline' },
      { to: '/today',        label: "Today's Actions", icon: CalendarCheck2, testid: 'nav-today' },
      { to: '/inquiries',    label: 'Inquiries',      icon: Mail,           testid: 'nav-inquiries',
        badgeKey: 'newInquiries',
        roles: ['super_user', 'admin'] },
      { to: '/executive-bi', label: 'Executive BI',   icon: Trophy,         testid: 'nav-executive-bi',
        roles: ['super_user'] },
      { to: '/vault',        label: 'Admin Vault',    icon: ShieldCheck,    testid: 'nav-vault',
        roles: ['super_user', 'admin'] },
      { to: '/underpayment', label: 'Underpayment Recovery', icon: Wallet, testid: 'nav-underpayment',
        roles: ['super_user', 'admin'] },
      { to: '/denied-claims', label: 'Denied Claims', icon: FileText, testid: 'nav-denied-claims',
        roles: ['super_user', 'admin'] },
    ] },
  { section: 'HR',
    items: [
      { to: '/hr',      label: 'HR Command Center', icon: HeartHandshake, testid: 'nav-hr',
        badgeKey: 'hrReview',
        roles: ['super_user', 'hr_payroll'] },
      { to: '/payroll', label: 'Payroll',           icon: DollarSign,     testid: 'nav-payroll',
        roles: ['super_user', 'hr_payroll'] },
    ] },
  { section: 'Clinics',
    items: [
      { to: '/clinics/clinic_katy',          label: 'Katy',          icon: Star,     testid: 'nav-clinic-katy',          clinicName: 'Katy' },
      { to: '/clinics/clinic_missouri_city', label: 'Missouri City', icon: Anchor,   testid: 'nav-clinic-missouri-city', clinicName: 'Missouri City' },
      { to: '/clinics/clinic_townelake',     label: 'Townelake',     icon: TreePine, testid: 'nav-clinic-townelake',     clinicName: 'Townelake' },
      { to: '/clinics/clinic_fry_rd',        label: 'Fry Rd',        icon: Flame,    testid: 'nav-clinic-fry-rd',        clinicName: 'Fry Rd' },
      { to: '/clinics/clinic_memorial',      label: 'Memorial',      icon: Cross,    testid: 'nav-clinic-memorial',      clinicName: 'Memorial' },
      { to: '/clinics/clinic_brookshire',    label: 'Brookshire',    icon: Home,     testid: 'nav-clinic-brookshire',    clinicName: 'Brookshire' },
      { to: '/clinics/clinic_tomball',       label: 'Tomball',       icon: Building2, testid: 'nav-clinic-tomball',      clinicName: 'Tomball' },
      { to: '/clinics/clinic_magnolia',      label: 'Magnolia',      icon: Landmark, testid: 'nav-clinic-magnolia',      clinicName: 'Magnolia' },
      { to: '/clinics/clinic_atascocita',    label: 'Atascocita',    icon: Sunrise,  testid: 'nav-clinic-atascocita',    clinicName: 'Atascocita' },
    ] },
  { section: 'Clinical',
    items: [
      { to: '/performance',    label: 'Clinician Performance', icon: TrendingUp, testid: 'nav-performance',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/pending-notes',  label: 'Pending Notes',         icon: FileText,   testid: 'nav-pending-notes',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'] },
      { to: '/low-deductible', label: 'Low Deductible',        icon: FileSpreadsheet, testid: 'nav-low-deductible',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/clinicians',     label: 'Clinician Roster',      icon: UsersIcon,  testid: 'nav-clinicians',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'] },
      { to: '/od-digest',      label: 'OD Digest',             icon: Award,      testid: 'nav-od-digest',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/monthly-report', label: 'Monthly Report',        icon: BarChart3,  testid: 'nav-monthly-report',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
    ] },
  { section: 'Settings',
    items: [
      { to: '/import',        label: 'Data Import',    icon: Upload,       testid: 'nav-import',
        roles: ['super_user', 'data_import'] },
      { to: '/referral-links',label: 'Referral Links', icon: Link2,        testid: 'nav-referral-links',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/setup',         label: 'Setup',          icon: Settings,     testid: 'nav-setup',
        roles: ['super_user', 'admin', 'hr_payroll'] },
      { to: '/users',         label: 'Users',          icon: UserCog,      testid: 'nav-users',
        roles: ['super_user'] },
      { to: '/help',          label: 'Help & Guide',   icon: HelpCircle,   testid: 'nav-help' },
      { to: '/feedback',      label: 'Feedback',       icon: MessageSquare, testid: 'nav-feedback' },
    ] },
];

const NAV_LUDO = [
  { section: 'Ludo ABA', items: [
      { to: '/ludo/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'nav-ludo-dashboard',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'] },
      { to: '/ludo/pipeline', label: 'Client Pipeline', icon: UsersIcon, testid: 'nav-ludo-pipeline',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'] },
      { to: '/ludo/schedule', label: 'Scheduling', icon: CalendarDays, testid: 'nav-ludo-schedule',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'] },
      { to: '/inquiries', label: 'Online Inquiries', icon: Mail, testid: 'nav-inquiries', badgeKey: 'newInquiries',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/referral-links', label: 'Referral Links', icon: Link2, testid: 'nav-referral-links',
        roles: ['super_user', 'admin', 'hr_payroll', 'office_director'] },
      { to: '/vault', label: 'Admin Vault', icon: ShieldCheck, testid: 'nav-vault', roles: ['super_user', 'admin', 'hr_payroll'] },
  ] },
  { section: 'Admin', items: [
      { to: '/users', label: 'Users', icon: UserCog, testid: 'nav-users', roles: ['super_user'] },
      { to: '/help', label: 'Help & Guide', icon: HelpCircle, testid: 'nav-help' },
      { to: '/feedback', label: 'Feedback', icon: MessageSquare, testid: 'nav-feedback' },
  ] },
];


function BrandHeader({ pinned, onTogglePin, onClose }) {
  return (
    <div className="px-5 py-5 flex items-center gap-3">
      <img
        src={ORG_META[getOrg()].logo || BRAND_LOGO}
        alt={ORG_META[getOrg()].full}
        className="w-14 h-14 rounded-full shadow-sm flex-shrink-0"
        data-testid="sidebar-logo"
      />
      <div className="leading-none min-w-0 flex-1">
        <div
          className={`font-serif text-[28px] font-medium text-[#2D2833] leading-[1] ${getOrg() === 'ludo' ? 'ludo-wordmark !text-[22px] whitespace-nowrap' : ''}`}
          style={getOrg() === 'ludo' ? undefined : { fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
        >
          {getOrg() === 'ludo' ? 'Ludo ABA' : ORG_META[getOrg()].name}
        </div>
        <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-[#8A7B95] font-medium whitespace-nowrap" data-testid="sidebar-org-sub">
          {ORG_META[getOrg()].sub}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onTogglePin}
          data-testid="sidebar-pin-toggle"
          aria-label={pinned ? 'Unpin sidebar (auto-hide on click)' : 'Pin sidebar (stay open)'}
          title={pinned ? 'Unpin — auto-hide' : 'Pin — stay open'}
          className="w-8 h-8 rounded-full flex items-center justify-center text-[#8A7B95] hover:text-[#4A3B5F] hover:bg-[#F1EBE0]/60 transition-colors"
        >
          {pinned ? <PinOff size={15} strokeWidth={1.8} /> : <Pin size={15} strokeWidth={1.8} />}
        </button>
        {!pinned && (
          <button
            onClick={onClose}
            data-testid="sidebar-close-btn"
            aria-label="Close sidebar"
            title="Close sidebar"
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#8A7B95] hover:text-[#4A3B5F] hover:bg-[#F1EBE0]/60 transition-colors"
          >
            <PanelLeftClose size={15} strokeWidth={1.8} />
          </button>
        )}
      </div>
    </div>
  );
}


export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [newInquiries, setNewInquiries] = useState(0);
  const [hrReview, setHrReview] = useState({ review: 0, over48: 0 });
  const [tourOn, setTourOn] = useState(false);

  const fetchBadge = useCallback(async () => {
    if (!user || !['super_user','admin','hr_payroll','office_director'].includes(user.role) || !(user.orgs || []).includes(getOrg())) return;
    try {
      const token = localStorage.getItem('thrive_session_token');
      const res = await fetch(`${API}/api/inquiries/new-referral-count`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Org': getOrg() },
      });
      if (!res.ok) return;
      const j = await res.json();
      setNewInquiries(Number(j?.count || 0));
    } catch (_) { /* silent */ }
  }, [user]);

  const fetchHrBadge = useCallback(async () => {
    if (!user || !['super_user','hr_payroll'].includes(user.role) || getOrg() === 'ludo') return;
    try {
      const token = localStorage.getItem('thrive_session_token');
      const res = await fetch(`${API}/api/hr/intake/counts`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Org': getOrg() },
      });
      if (!res.ok) return;
      const j = await res.json();
      setHrReview({
        review: Number(j?.review || 0),
        over48: Number(j?.waiting_over_48h || 0),
      });
    } catch (_) { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchBadge();
    fetchHrBadge();
    const iv = setInterval(() => { fetchBadge(); fetchHrBadge(); }, 60_000);
    const onChange = () => fetchBadge();
    const onHrChange = () => fetchHrBadge();
    window.addEventListener('inquiries:changed', onChange);
    window.addEventListener('hr-intake:changed', onHrChange);
    return () => {
      clearInterval(iv);
      window.removeEventListener('inquiries:changed', onChange);
      window.removeEventListener('hr-intake:changed', onHrChange);
    };
  }, [fetchBadge, fetchHrBadge]);

  // Keep the active company valid for this user and the current page inside it.
  const _org = getOrg();
  const orgReady = !user?.orgs?.length || (user.orgs.includes(_org)
    && (_org !== 'ludo' || LUDO_PATHS.some((p) => location.pathname.startsWith(p)))
    && !(_org === 'thrive' && location.pathname.startsWith('/ludo')));
  useEffect(() => {
    if (!user?.orgs?.length) return;
    const org = getOrg();
    if (!user.orgs.includes(org)) {
      setOrg(user.orgs[0]);
      window.location.replace(user.orgs[0] === 'ludo' ? '/ludo/dashboard' : '/dashboard');
      return;
    }
    if (org === 'ludo' && !LUDO_PATHS.some((p) => location.pathname.startsWith(p))) {
      navigate('/ludo/dashboard', { replace: true });
    }
    if (org === 'thrive' && location.pathname.startsWith('/ludo')) navigate('/dashboard', { replace: true });
  }, [user, location.pathname, navigate]);

  // First-login OnboardingTour auto-launch.
  useEffect(() => {
    if (!user?.email) return;
    const key = `thrive_tour_done_${user.email}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setTourOn(true), 600);
      return () => clearTimeout(t);
    }
  }, [user?.email]);

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');
  // OD's clinic scope — a single-source list that mirrors the backend
  // /auth/me `clinics` field (backfilled from clinicians.office_director_of).
  // Used to hide the 8 other clinic tabs from an OD.
  const isOD = user?.role === 'office_director';
  const odClinics = new Set(
    isOD ? (user?.clinics?.length ? user.clinics : (user?.clinic ? [user.clinic] : [])) : []
  );
  const canSee = (item, sectionName) => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    // Filter clinic tabs to only the OD's own clinic. If the OD has no
    // clinic scope (roster misconfig), they see zero clinic tabs.
    if (sectionName === 'Clinics' && isOD) {
      return item.clinicName && odClinics.has(item.clinicName);
    }
    return true;
  };

  // ── Collapsible sidebar (iter 326) ──────────────────────────────────
  // Sidebar auto-hides so it doesn't eat screen real estate. Reveal via
  // (a) hovering the thin left edge trigger, (b) clicking the floating
  // menu button, or (c) pinning it open. Clicking any nav link auto-
  // hides again unless pinned. Pin state persists per-user in
  // localStorage. During the first-login OnboardingTour we force it
  // open so the spotlight sequence works.
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('thrive_sidebar_pinned') === '1'; }
    catch { return false; }
  });
  const [hoverOpen, setHoverOpen] = useState(false);
  // 100ms hover-intent delay — brushing the cursor past the menu button
  // during normal mouse travel shouldn't pop the sidebar. A deliberate
  // hover holds for at least this long before we reveal.
  const HOVER_INTENT_MS = 100;
  const hoverIntentRef = useRef(null);
  const scheduleOpen = () => {
    if (hoverIntentRef.current) clearTimeout(hoverIntentRef.current);
    hoverIntentRef.current = setTimeout(() => setHoverOpen(true), HOVER_INTENT_MS);
  };
  const cancelOpenIntent = () => {
    if (hoverIntentRef.current) {
      clearTimeout(hoverIntentRef.current);
      hoverIntentRef.current = null;
    }
  };
  useEffect(() => () => cancelOpenIntent(), []);
  // NOTE: OnboardingTour used to force the sidebar open via `|| tourOn`,
  // but that left users stuck with an overlapping sidebar for the whole
  // life of the tour (they'd click nav, the URL changed, and the sidebar
  // stayed on top of the page). Tour now runs independently — if it
  // needs to spotlight the sidebar, callers should pin it explicitly.
  const open = pinned || hoverOpen;
  useEffect(() => {
    try { localStorage.setItem('thrive_sidebar_pinned', pinned ? '1' : '0'); }
    catch {}
  }, [pinned]);
  // Auto-hide when route changes (nav click). Pinned sessions stay open.
  useEffect(() => {
    if (!pinned) setHoverOpen(false);
  }, [location.pathname, pinned]);
  const togglePin = () => setPinned((v) => !v);

  return (
    <div className="min-h-screen bg-[#F6F3EE] text-[#2E2432] relative">
      {/* Edge hover trigger — invisible 6px strip on left edge that opens the sidebar on hover */}
      {!open && (
        <div
          data-testid="sidebar-hover-trigger"
          onMouseEnter={scheduleOpen}
          onMouseLeave={cancelOpenIntent}
          className="fixed top-0 left-0 h-full w-2 z-40"
          aria-hidden="true"
        />
      )}

      {/* Floating menu button — comfortably clickable but not obtrusive */}
      {!open && (
        <button
          type="button"
          data-testid="sidebar-open-btn"
          onMouseEnter={scheduleOpen}
          onMouseLeave={cancelOpenIntent}
          onFocus={() => setHoverOpen(true)}
          onClick={() => { cancelOpenIntent(); setHoverOpen(true); }}
          aria-label="Open sidebar"
          className="fixed top-4 left-4 z-50 w-11 h-11 rounded-full bg-[#FBF8F3] border border-[#E9E1D5] shadow-md flex items-center justify-center text-[#4A3B5F] hover:bg-[#F1EBE0] hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
        >
          <Menu size={20} strokeWidth={1.8} />
        </button>
      )}

      {/* Backdrop — dims content when sidebar is open in hover mode; clicking it closes */}
      {hoverOpen && !pinned && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setHoverOpen(false)}
          className="fixed inset-0 z-30 bg-[#2E2432]/20 backdrop-blur-[1px] transition-opacity duration-200"
          aria-hidden="true"
        />
      )}

      <aside
        data-testid="sidebar"
        data-open={open ? 'true' : 'false'}
        data-pinned={pinned ? 'true' : 'false'}
        onMouseLeave={() => { if (!pinned) setHoverOpen(false); }}
        className={`fixed top-0 left-0 h-full w-64 ${getOrg() === 'ludo' ? 'bg-[#F4F2F7]' : 'bg-[#FBF8F3]'} border-r border-[#E9E1D5]/70 flex flex-col z-40 transform transition-transform duration-300 ease-out will-change-transform ${
          open ? 'translate-x-0 shadow-2xl' : '-translate-x-full shadow-none'
        }`}
      >
        {/* Brand header — reuses the login-page logo + serif treatment */}
        <BrandHeader pinned={pinned} onTogglePin={togglePin} onClose={() => setHoverOpen(false)} />
        <OrgSwitcher orgs={user?.orgs} />

        <nav className="flex-1 overflow-y-auto overscroll-contain sidebar-scroll py-3">
          {(getOrg() === 'ludo' ? NAV_LUDO : NAV).map((sec) => {
            const visible = sec.items.filter((it) => canSee(it, sec.section));
            if (visible.length === 0) return null;
            return (
              <div key={sec.section} className="mb-4">
                <div className="px-6 pt-3 pb-2 text-[10.5px] uppercase tracking-[0.14em] text-[#B8ADB9] font-semibold">
                  {sec.section}
                </div>
                {visible.map((it) => {
                  const Icon = it.icon;
                  const active = isActive(it.to);
                  return (
                    <div key={it.to} className="px-3">
                      <Link
                        to={it.to}
                        data-testid={it.testid}
                        onClick={() => { if (!pinned) setHoverOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-1.5 rounded-full text-[14px] transition-colors ${
                          active
                            ? 'bg-[#EFE7F1] text-[#2D2833] font-medium shadow-[inset_0_0_0_1px_rgba(74,59,95,0.08)]'
                            : 'text-[#2D2833] font-normal hover:bg-[#F1EBE0]/60'
                        }`}
                      >
                        <Icon size={16} strokeWidth={1.6} className={active ? 'text-[#4A3B5F]' : 'text-[#6E5F78]'} />
                        <span className="flex-1 truncate">{it.label}</span>
                        {it.badgeKey === 'newInquiries' && newInquiries > 0 && (
                          <span
                            data-testid="badge-new-inquiries"
                            className="ml-1 inline-flex items-center justify-center text-[10px] font-semibold bg-[#E56F79] text-white rounded-full px-1.5 min-w-[18px] h-[18px] animate-pulse-3s"
                          >
                            {newInquiries}
                          </span>
                        )}
                        {it.badgeKey === 'hrReview' && hrReview.review > 0 && (
                          <span
                            data-testid="badge-hr-review"
                            title={hrReview.over48 > 0 ? `${hrReview.over48} waiting over 48h` : `${hrReview.review} awaiting review`}
                            className={`ml-1 inline-flex items-center justify-center text-[10px] font-semibold text-white rounded-full px-1.5 min-w-[18px] h-[18px] ${hrReview.over48 > 0 ? 'animate-pulse-3s' : ''}`}
                            style={{ background: hrReview.over48 > 0 ? '#E56F79' : '#B08A3A' }}
                          >
                            {hrReview.review}
                          </span>
                        )}
                      </Link>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User + logout */}
        <div className="px-6 py-3 border-t border-[#E9E1D5]/70">
          <div className="text-[12px] text-[#5A4C68] truncate" title={user?.email}>{user?.email}</div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8ADB9] mt-0.5">{user?.role || 'guest'}</div>
          <button
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            data-testid="btn-logout"
            className="mt-2 flex items-center gap-2 py-1 text-[12px] text-[#8A7B95] hover:text-[#4A3B5F] transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <main
        data-testid="app-main"
        className={`min-h-screen overflow-x-clip transition-[padding] duration-300 ease-out ${pinned ? 'pl-64' : 'pl-16'}`}
      >
        {orgReady ? children : null}
      </main>

      {tourOn && (
        <OnboardingTour
          onDone={() => {
            if (user?.email) localStorage.setItem(`thrive_tour_done_${user.email}`, '1');
            setTourOn(false);
          }}
        />
      )}
    </div>
  );
}
